import React from "react";
import { useTranslation } from "react-i18next";
import ChevronRight from "@/assets/icons/chevronRight.svg?react";
import { DownloadIcon } from "@/components/atomic/DownloadIcon";
import VocsView from "@/components/ui/VocsView";
import { useDownloadMessageVocs } from "@/hooks/useDownloadMessageVocs";
import { useMessageVocs } from "@/hooks/useMessageVocs";
import { cn } from "@/lib/utils";
import { AttributeFilters } from "@/models/attributeFilter";

interface VocSidePanelProps {
  isOpen: boolean;
  onToggle: () => void;
  conversationId: string | null;
  messageId: string | null;
  className?: string;
  attributeFilters: AttributeFilters;
}

export const VocSidePanel: React.FC<VocSidePanelProps> = ({
  isOpen,
  onToggle,
  conversationId,
  messageId,
  className,
  attributeFilters,
}) => {
  const { t } = useTranslation("voiceek_ui");
  const {
    vocs: selectedMessageVocs,
    hasMore,
    total,
    loadMoreVocs,
    isLoading,
    isFetchingMore,
  } = useMessageVocs(conversationId, messageId);

  const { handleDownload, isDownloading } = useDownloadMessageVocs(conversationId, messageId);

  const visibleAttributeNames = Object.keys(attributeFilters);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-500">読み込み中...</div>
        </div>
      );
    }

    if (selectedMessageVocs && selectedMessageVocs.length > 0) {
      return (
        <>
          <div className="flex justify-end w-full items-center">
            <button
              disabled={isDownloading}
              className={cn(
                "flex items-center text-[#6888E1]",
                "hover:text-[#3C569D] hover:cursor-pointer",
              )}
              onClick={handleDownload}
            >
              <span className="w-[26px] h-[26px] items-center flex">
                <DownloadIcon className="w-[17px] h-[17px]" isLoading={isDownloading} />
              </span>
              <span className="font-semibold">{t("chat.download.button")}</span>
            </button>
          </div>
          <VocsView
            vocs={selectedMessageVocs}
            title={total > 0 ? `お客様の声 (${total}件)` : `お客様の声`}
            innerScrollable
            hasMore={hasMore}
            onLoadMore={loadMoreVocs}
            isLoading={isFetchingMore}
            visibleAttributeNames={visibleAttributeNames}
          />
        </>
      );
    }

    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">関連するVoCがありません</div>
      </div>
    );
  };

  return (
    <>
      {/* サイドパネル */}
      <div
        className={cn(
          "right-0 top-0 h-full ml-[21px] bg-white border-l-1 border-[#B7B7B7] transition-width duration-200 z-50",
          className,
        )}
        style={{ width: isOpen ? "600px" : "0px" }}
        data-testid="voc-side-panel"
      >
        <div className="relative flex h-full">
          <div className="absolute top-40 left-[-21px] z-10">
            <button
              onClick={onToggle}
              className={cn(
                "w-[21px] h-[69px] flex items-center justify-center border-1 border-r-0 border-[#B7B7B7]",
                "rounded-l-[10px] bg-white hover:bg-gray-50 transition-colors cursor-pointer",
              )}
              aria-label={isOpen ? "パネルを閉じる" : "パネルを開く"}
            >
              <ChevronRight className={isOpen ? "" : "-scale-x-100"} />
            </button>
          </div>

          {/* メインコンテンツエリア */}
          <div className="flex flex-col gap-2 bg-white px-8 py-2 h-full w-full min-h-0 overflow-hidden">
            {renderContent()}
          </div>
        </div>
      </div>
    </>
  );
};
